import React, { useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Checkbox, ListItemText, MenuItem } from "@mui/material";
import "../user-requests-filter.scss";
import { Formik, Form } from "formik";
import "./filter-options-user-requests.scss";
import { camelCase } from "lodash";
import FirstNameFilter from "../user-requests-filter-options-list/first-name-filter";
import LastNameFilter from "../user-requests-filter-options-list/last-name-filter";
import EmailFilter from "../../../../tenant/tenant-list/tenant-filter-options/email-filter";
import { useLogin } from "../../../../../common/hooks/useUser";
import dayjs from "dayjs";
import { UserRequestsFilterOptions } from "../../user-requests.constant";

const UserRequestsOptions: React.FC<{
  open: boolean;
  onClose: () => void;
  onApplyFilters: (filters: Record<string, any>, shouldClose?: boolean) => void;
  filters: Record<string, any>;
  companyNames?: any;
  roles?: any;
}> = ({ open, onClose, onApplyFilters, filters, companyNames, roles }) => {
  const [filterOptions, setFilterOptions] = useState(() =>
    UserRequestsFilterOptions.map((opt) => ({ ...opt }))
  );

  const handleCheckboxChange = (
    label: string,
    values: Record<string, any>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const updated = filterOptions.map((item) => {
      if (item.label === label) {
        const isChecked = !item.value;

        if (!isChecked) {
          const key = camelCase(label);
          setFieldValue(key, Array.isArray(values[key]) ? [] : "");
        }

        return { ...item, value: isChecked };
      }
      return item;
    });

    setFilterOptions(updated);
  };

  const isAnyFilterSelected = filterOptions.some((opt) => opt.value);
  const tenantDetails = useLogin();
  const isSuperParter = useMemo(() => {
    return tenantDetails?.tenantDetails?.tenantType === "SuperTenant";
  }, [tenantDetails?.tenantDetails?.tenantType]);

  const getInitialValues = () => {
    const values: Record<string, any> = {};
    filterOptions.forEach((option) => {
      const key = camelCase(option.label);
      values[key] = "";
    });
    return values;
  };
  const [formValues, setFormValues] = useState(getInitialValues());

  const handleApply = (values: Record<string, any>) => {
    const selected = filterOptions
      .filter((opt) => opt.value)
      .reduce(
        (acc, curr) => {
          const key = camelCase(curr.label);
          acc[key] = values[key];
          return acc;
        },
        {} as Record<string, any>
      );
    setFormValues(values);
    onApplyFilters(selected);
  };

  const validate = (values: Record<string, any>) => {
    const errors: Record<string, string> = {};
    const minDate = dayjs("1900-01-01");
    filterOptions.forEach((option) => {
      if (option.value) {
        const key = camelCase(option.label);
        if (
          !values[key] ||
          (Array.isArray(values[key]) && values[key].length === 0)
        ) {
          errors[key] = `${option.label} cannot be empty`;
        }
      }
    });
    return errors;
  };

  const renderFilterField = (
    label: string,
    values: Record<string, any>,
    errors?: Record<string, any>,
    touched?: Record<string, any>,
    setFieldValue?: (field: string, value: any) => void
  ) => {
    switch (label) {
      case "Email":
        return (
          <EmailFilter
            isEmailEnabled={true}
            value={values["email"]}
            error={errors?.email}
            onChange={(e: any) => setFieldValue?.("email", e.target.value)}
          />
        );
      case "First Name":
        return (
          <FirstNameFilter
            isFirstNameEnabled={true}
            value={values["firstName"]}
            error={errors?.lastName}
            onChange={(e: any) => setFieldValue?.("firstName", e.target.value)}
          />
        );
      case "Last Name":
        return (
          <LastNameFilter
            isLastNameEnabled={true}
            value={values["lastName"]}
            error={errors?.lastName}
            onChange={(e: any) => setFieldValue?.("lastName", e.target.value)}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const updatedOptions = filterOptions.map((opt) => {
      let key: string;

      switch (opt.label) {
        default:
          key = camelCase(opt.label);
      }

      const isSelected = key in filters;

      return { ...opt, value: !!isSelected };
    });

    setFilterOptions(updatedOptions);

    setFormValues((prev) => ({
      ...getInitialValues(),
      ...filters,
    }));
  }, [filters]);

  return (
    <div className={`filter-drawer-usermgmt ${open ? "open" : ""}`}>
      <div className="filter-header">
        <div className="drawer-header">
          <h4>Filters</h4>
          <CloseIcon fontSize="small" onClick={onClose} />
        </div>
        <h4>Filter By Fields</h4>
      </div>

      <Formik
        initialValues={formValues}
        enableReinitialize
        validate={validate}
        onSubmit={handleApply}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          handleSubmit,
          resetForm,
        }) => {
          return (
            <Form className="drawer-content-user-mgmt" onSubmit={handleSubmit}>
              {filterOptions.map((option) => (
                <div key={option.label} className="filter-option-row">
                  <MenuItem
                    key={option.label}
                    value={option.label}
                    className="filter-menu-item"
                    onClick={() =>
                      handleCheckboxChange(option.label, values, setFieldValue)
                    }
                  >
                    <Checkbox checked={option.value} />
                    <ListItemText primary={option.label} />
                  </MenuItem>

                  {option.value &&
                    renderFilterField(
                      option.label,
                      values,
                      errors,
                      touched,
                      setFieldValue
                    )}
                </div>
              ))}

              {isAnyFilterSelected && (
                <div className="lead-filter-buttons">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => {
                      resetForm();
                      setFormValues(getInitialValues());
                      setFilterOptions(
                        filterOptions.map((opt) => ({ ...opt }))
                      );
                      onApplyFilters({}, false);
                    }}
                  >
                    Reset
                  </button>
                  <button type="submit" className="next-button-wrapper">
                    Apply
                  </button>
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default UserRequestsOptions;
